import { useCallback, useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DISPOSITIONS = [
  { code: "DC", label: "Data Captured" },
  { code: "PC", label: "Partial Capture" },
  { code: "CB", label: "Callback Scheduled" },
  { code: "RF", label: "Refused" },
  { code: "NA", label: "No Answer" },
  { code: "WN", label: "Wrong Number" },
  { code: "NR", label: "Not Reachable" },
];
const FOLLOWUP_REQUIRED = new Set(["PC", "CB"]);
// Fallbacks only — the live options come from the Dropdown Catalogue (§8.4).
const EMPLOYEE_BUCKETS = ["1-10", "11-50", "51-100", "101-500", "500+"];
const SECTORS = ["Retail", "Hospitality", "Healthcare", "Education", "Manufacturing", "Services", "Other"];


/** Lead form state, config-driven field helpers (§8.3/§8.4), save + validation.
 *  Extracted from pages/LeadDetail.jsx (refactor Phase 3) — logic unchanged. */
export function useLeadForm(id, opts = {}) {
  const [lead, setLead] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [fieldCfg, setFieldCfg] = useState({});      // §8.3 — key → config
  const [customFields, setCustomFields] = useState([]); // §8.3 — admin-added fields
  const [dropdowns, setDropdowns] = useState({});    // §8.4 — catalogue options by key
  const initialFormRef = useRef(null);               // snapshot for dirty-detection

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/related`),
        api.get("/lead-fields").catch(() => ({ data: { fields: [] } })),
        api.get("/lead-dropdowns").catch(() => ({ data: { grouped: {} } })),
      ]);
      setLead(r1.data);
      setRelated(r2.data?.related || []);
      const cfgMap = {};
      (r3.data.fields || []).forEach((f) => { cfgMap[f.key] = f; });
      setFieldCfg(cfgMap);
      setCustomFields((r3.data.fields || []).filter((f) => !f.system && f.visible));
      setDropdowns(r4.data?.grouped || {});
      const initial = {
        employee_count: r1.data.employee_count || "",
        sector: r1.data.sector || "",
        disposition: r1.data.disposition || "",
        follow_up_at: r1.data.follow_up_at || "",
        call_language: r1.data.call_language || "english",
        remarks: r1.data.remarks || "",
        poc_name: r1.data.poc?.name || "",
        poc_phone: r1.data.poc?.phone || "",
        poc_email: r1.data.poc?.email || "",
        poc_confirmed: !!r1.data.poc?.confirmed_from_upload,
        hq_has: r1.data.head_office?.has ? "yes" : "no",
        hq_name: r1.data.head_office?.name || "",
        hq_phone: r1.data.head_office?.phone || "",
        hq_email: r1.data.head_office?.email || "",
        custom: r1.data.custom || {},
      };
      initialFormRef.current = JSON.parse(JSON.stringify(initial));
      setForm(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);


  // Follow-up rule is exempted for tenants where the field is disabled (Ooredoo SOHO).
  const followUpNeeded = !opts.followUpExempt && FOLLOWUP_REQUIRED.has(form.disposition);
  const isDisabledSave = followUpNeeded && !form.follow_up_at;

  // Dirty-detection vs the snapshot loaded from the server.
  const isDirty = initialFormRef.current
    ? JSON.stringify(form) !== JSON.stringify(initialFormRef.current)
    : false;

  // §8.3 config helpers — default to visible/optional when no config row exists.
  const cfgOf = (key) => fieldCfg[key] || {};
  const show = (key) => cfgOf(key).visible !== false;
  const req = (key) => !!cfgOf(key).required;
  const ro = (key) => !!cfgOf(key).read_only;
  const labelOf = (key, fallback) => cfgOf(key).label || fallback;
  // §8.4 catalogue options with hardcoded fallback when the key has no rows.
  const optionsFor = (ddKey, fallback) => {
    const rows = (dropdowns[ddKey] || []).filter((o) => o.active !== false);
    if (rows.length) {
      return rows.map((o) => ({
        value: o.value, label: o.label || o.value,
        color: o.color, ownership_group: o.ownership_group,
      }));
    }
    return fallback;
  };
  const empOptions = optionsFor("employee_count", EMPLOYEE_BUCKETS.map((v) => ({ value: v, label: v })));
  const sectorOptions = optionsFor("sector", SECTORS.map((v) => ({ value: v, label: v })));
  const dispOptions = optionsFor("disposition", DISPOSITIONS.map((d) => ({ value: d.code, label: d.label })));

  // Mandatory disposition (config-driven, consistent with Field Management):
  // once any field is edited, a lead may not be saved with a blank disposition.
  // An existing disposition is preserved (no re-selection required).
  const dispositionRequired = req("disposition");
  const needsDisposition = dispositionRequired && !form.disposition && isDirty;

  // Mandatory fields (§4.4/§8.3) are enforced when the agent claims a full
  // capture (disposition = DC). Follow-up has its own PC/CB rule above.
  const missingRequired = () => {
    if (form.disposition !== "DC") return [];
    const miss = [];
    const check = (key, val, fallback) => {
      if (show(key) && req(key) && !String(val || "").trim()) miss.push(labelOf(key, fallback));
    };
    check("employee_count", form.employee_count, "Employee Count");
    check("sector", form.sector, "Sector");
    check("remarks", form.remarks, "Remarks");
    check("poc_name", form.poc_name, "POC Name");
    check("poc_phone", form.poc_phone, "POC Contact");
    check("poc_email", form.poc_email, "POC Email");
    check("hq_name", form.hq_name, "HQ Name");
    check("hq_phone", form.hq_phone, "HQ Contact");
    check("hq_email", form.hq_email, "HQ Email");
    customFields.forEach((f) => {
      if (f.required && !String((form.custom || {})[f.key] || "").trim()) miss.push(f.label);
    });
    return miss;
  };

  const save = async () => {
    if (needsDisposition) {
      toast.error("Disposition is mandatory. Please select a disposition before saving the lead.");
      return;
    }
    if (isDisabledSave) {
      toast.error("Follow-up date/time is required for this disposition.");
      return;
    }
    const miss = missingRequired();
    if (miss.length) {
      toast.error(`Mandatory field(s) missing for Data Captured: ${miss.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_count: form.employee_count || null,
        sector: form.sector || null,
        disposition: form.disposition || null,
        follow_up_at: form.follow_up_at || null,
        call_language: form.call_language || null,
        remarks: form.remarks || null,
        custom: form.custom || {},
        poc: {
          name: form.poc_name || "",
          phone: form.poc_phone || "",
          email: form.poc_email || "",
          confirmed_from_upload: !!form.poc_confirmed,
        },
        head_office: {
          has: form.hq_has === "yes",
          name: form.hq_name || "",
          phone: form.hq_phone || "",
          email: form.hq_email || "",
        },
      };
      const { data } = await api.patch(`/leads/${id}`, payload);
      toast.success(
        data.propagated_leads > 0
          ? `Saved. HQ propagated to ${data.propagated_leads} related lead(s).`
          : "Saved."
      );
      if (data?.lead) setLead(data.lead);
      initialFormRef.current = JSON.parse(JSON.stringify(form));
      // refresh related in case propagation affected them
      const rel = await api.get(`/leads/${id}/related`);
      setRelated(rel.data?.related || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleWrongNumber = async (e164, current) => {
    try {
      const { data } = await api.post(`/leads/${id}/wrong-number`, {
        e164, wrong_number: !current,
      });
      if (data?.lead) setLead(data.lead);
      toast.success(current ? "Number restored to rotation" : "Number flagged as Wrong Number");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  // POC checkbox auto-fill helpers
  const handleTogglePocConfirm = (checked) => {
    setForm((f) => {
      if (checked) {
        const firstOk = (lead?.numbers || []).find((n) => !n.wrong_number);
        return {
          ...f,
          poc_confirmed: true,
          poc_name: f.poc_name || lead?.contact_name || "",
          poc_phone: f.poc_phone || firstOk?.e164 || "",
        };
      }
      return { ...f, poc_confirmed: false };
    });
  };

  return {
    lead, setLead, related, loading, saving, form, setForm,
    fieldCfg, customFields, dropdowns, load,
    show, req, ro, labelOf, followUpNeeded, isDisabledSave,
    empOptions, sectorOptions, dispOptions,
    isDirty, dispositionRequired, needsDisposition,
    save, toggleWrongNumber, handleTogglePocConfirm,
  };
}
